import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import { saveBlob } from '../utils/saveBlob';
import { parseContentDispositionFilename } from '../utils/contentDisposition';

export type StatementFormat = 'pdf' | 'xlsx';

/** Server-built table shared by the on-screen view and the PDF/XLSX downloads. */
export interface StatementTable {
    title: string;
    meta: string[];
    columns: { header: string; weight: number; numeric: boolean }[];
    rows: { kind: 'SECTION' | 'DATA' | 'GROUP' | 'TOTAL' | 'GRAND'; indent: number; cells: (string | number | { value: number | null } | null)[] }[];
    notes: string[];
}

const institution = () => AuthService.getSelectedInstitution()!;

const download = async (url: string, params: Record<string, string | undefined>, fallback: string) => {
    const response = await axios.get(url, { params, responseType: 'blob' });
    const disposition: string = response.headers?.['content-disposition'] ?? '';
    saveBlob(new Blob([response.data]), parseContentDispositionFilename(disposition, fallback));
};

export const FinancialStatementService = {
    getEquityStatement: async (from: string, to: string): Promise<StatementTable> => {
        const response = await axios.get(ENDPOINTS.FINANCIAL_STATEMENTS.EQUITY(institution()), { params: { from, to } });
        return response.data;
    },
    downloadEquityStatement: (from: string, to: string, format: StatementFormat) =>
        download(ENDPOINTS.FINANCIAL_STATEMENTS.EQUITY_EXPORT(institution()), { from, to, format }, `equity-statement-${to}.${format}`),
    getNotes: async (asOf: string): Promise<StatementTable> => {
        const response = await axios.get(ENDPOINTS.FINANCIAL_STATEMENTS.NOTES(institution()), { params: { asOf } });
        return response.data;
    },
    downloadNotes: (asOf: string, format: StatementFormat) =>
        download(ENDPOINTS.FINANCIAL_STATEMENTS.NOTES_EXPORT(institution()), { asOf, format }, `notes-${asOf}.${format}`),
    downloadCashFlow: (from: string, to: string, format: StatementFormat, segmentId?: string) =>
        download(ENDPOINTS.FINANCIAL_STATEMENTS.CASH_FLOW_EXPORT(institution()), { from, to, format, segmentId }, `cash-flow-${to}.${format}`),
};
